import React, { useRef } from 'react';
import type { TeamData } from 'types/websiteBuilder';
import { compressImageFile } from 'utils/imageUtils';
import avatarPlaceholder from 'assets/websiteBuilderDefaults/avatar-placeholder.svg';
import styles from './TeamSection.module.scss';

interface TeamSectionEditorProps {
  data: Record<string, unknown>;
  onUpdateData: (data: Record<string, unknown>) => void;
}

function getData(data: Record<string, unknown>): TeamData {
  const items = Array.isArray(data.items)
    ? (data.items as Record<string, unknown>[]).map((i) => ({
        name: typeof i.name === 'string' ? i.name : '',
        role: typeof i.role === 'string' ? i.role : '',
        bio: typeof i.bio === 'string' ? i.bio : '',
        imageUrl: typeof i.imageUrl === 'string' ? i.imageUrl : '',
      }))
    : [];
  return {
    title: typeof data.title === 'string' ? data.title : 'Our Team',
    items:
      items.length > 0
        ? items
        : [{ name: '', role: '', bio: '', imageUrl: '' }],
  };
}

export function TeamSectionEditor({
  data,
  onUpdateData,
}: TeamSectionEditorProps) {
  const d = getData(data);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadIndexRef = useRef<number>(0);

  const updateItem = (index: number, field: string, value: string) => {
    const next = [...(d.items ?? [])];
    if (!next[index])
      next[index] = { name: '', role: '', bio: '', imageUrl: '' };
    (next[index] as Record<string, string>)[field] = value;
    onUpdateData({ ...data, items: next });
  };

  const addItem = () => {
    onUpdateData({
      ...data,
      items: [
        ...(d.items ?? []),
        { name: '', role: '', bio: '', imageUrl: avatarPlaceholder },
      ],
    });
  };

  const removeItem = (index: number) => {
    const next = (d.items ?? []).filter((_, i) => i !== index);
    onUpdateData({ ...data, items: next });
  };

  const handleImageFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file == null) return;
    const index = uploadIndexRef.current;
    compressImageFile(file)
      .then((imageUrl) => updateItem(index, 'imageUrl', imageUrl))
      .catch(() => {});
    e.target.value = '';
  };

  const triggerImageUpload = (index: number) => {
    uploadIndexRef.current = index;
    fileInputRef.current?.click();
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className={styles.fileInput}
        onChange={handleImageFileChange}
        aria-label="Upload photo"
      />
      <input
        type="text"
        className={styles.titleInput}
        value={d.title}
        onChange={(e) => onUpdateData({ ...data, title: e.target.value })}
        placeholder="Section title"
      />
      <div className={styles.grid}>
        {(d.items ?? []).map((item, i) => (
          <div key={i} className={styles.card}>
            <div className={styles.imageUrlRow}>
              <input
                type="text"
                value={item.imageUrl}
                onChange={(e) => updateItem(i, 'imageUrl', e.target.value)}
                placeholder="Image URL"
              />
              <button
                type="button"
                className={styles.browseBtn}
                onClick={() => triggerImageUpload(i)}
              >
                Browse
              </button>
              <button
                type="button"
                className={styles.clearBtn}
                onClick={() => updateItem(i, 'imageUrl', '')}
              >
                Clear
              </button>
            </div>
            <input
              type="text"
              value={item.name}
              onChange={(e) => updateItem(i, 'name', e.target.value)}
              placeholder="Enter Name here"
            />
            <input
              type="text"
              value={item.role}
              onChange={(e) => updateItem(i, 'role', e.target.value)}
              placeholder="Enter Role here"
            />
            <textarea
              value={item.bio}
              onChange={(e) => updateItem(i, 'bio', e.target.value)}
              placeholder="Enter Bio here"
              rows={2}
            />
            <button
              type="button"
              className={styles.removeBtn}
              onClick={() => removeItem(i)}
            >
              Remove
            </button>
          </div>
        ))}
      </div>
      <button type="button" className={styles.addBtn} onClick={addItem}>
        + Add member
      </button>
    </>
  );
}

interface TeamSectionProps {
  data: Record<string, unknown>;
  onUpdateData: (data: Record<string, unknown>) => void;
}

function TeamSection({ data }: TeamSectionProps) {
  const d = getData(data);

  return (
    <section className={styles.section}>
      <div className={styles.inner}>
        <h2 className={styles.title}>{d.title}</h2>
        <div className={styles.grid}>
          {(d.items ?? []).map((item, i) => (
            <div key={i} className={styles.card}>
              <div className={styles.imageWrap}>
                <img
                  src={item.imageUrl || avatarPlaceholder}
                  alt=""
                  className={styles.image}
                />
              </div>
              <h3
                className={`${styles.name} ${item.name ? '' : styles.placeholderText}`}
              >
                {item.name || 'Enter Name here'}
              </h3>
              <p
                className={`${styles.role} ${item.role ? '' : styles.placeholderText}`}
              >
                {item.role || 'Enter Role here'}
              </p>
              <p
                className={`${styles.bio} ${item.bio ? '' : styles.placeholderText}`}
              >
                {item.bio || 'Enter Bio here'}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default TeamSection;
